import { createSignal, createEffect, createRenderEffect, createMemo, Show, For, onMount, onCleanup, untrack } from 'solid-js';
import { useSearchParams, useNavigate } from '@solidjs/router';
import { useApi, isOk } from '../api';
import type { BotMsgEntryDto } from '../api';
import { subscribeBotEngineEvents, subscribeBotEngineStream } from '../services/botEngineEvents';
import { subscribeDomainStream, type OnlinePlayer } from '../services/onlinePlayersApi';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useActiveBot } from '../state/activeBot';
import { useMessageUnread } from '../state/messageUnread';
import { useMutedContacts } from '../state/mutedContacts';
import { pushSubscribed, refreshPushSubscribed } from '../services/pwa';
import BotScoped from '../components/BotScoped';
import Text from '../components/Text';
import { SearchInput } from '../components/SearchInput';
import { EmptyState } from '../components/EmptyState';
import { formatMinecraftText } from '../components/MinecraftText';
import { RelativeTime } from '../components/RelativeTime';
import PlayerHeadAvatar from '../components/PlayerHeadAvatar';
import styles from './Messages.module.css';
import { firstError } from '../api/envelope';

type Msg = BotMsgEntryDto;
type OutboxEntry = { id: string; contact: string; body: string; state: 'sending' | 'sent'; lastStoredOutgoingIdAtSend: string | null };

// Live messages arrive over the event stream; this poll only swaps them for stored rows.
const FALLBACK_POLL_MS = 20_000;
const AT_BOTTOM_SLACK_PX = 48;
const SEND_ERROR_KEYS: Record<string, string> = {
    PLAYER_NOT_FOUND: 'MESSAGES_PLAYER_NOT_FOUND',
    MESSAGE_UNCONFIRMED: 'MESSAGES_SEND_UNCONFIRMED',
    SEND_FAILED: 'MESSAGES_SEND_FAILED',
};
const strip = (s: string) => (s ?? '').replace(/§./g, '');
const comparable = (text: string) => strip(text).trim().toLowerCase();
const sameContact = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
const draftKey = (botId: string, contact: string) => `vektor.msgs.draft.${botId}.${contact.toLowerCase()}`;

function readStoredDraft(botId: string, contact: string): string {
    try { return localStorage.getItem(draftKey(botId, contact)) ?? ''; } catch { return ''; }
}

function storeDraft(botId: string, contact: string, text: string) {
    try {
        if (text) localStorage.setItem(draftKey(botId, contact), text);
        else localStorage.removeItem(draftKey(botId, contact));
    } catch { /* private mode */ }
}

function elapsed(iso: string | null, now: number): string {
    if (!iso) return '-';
    const then = new Date(iso).getTime();
    if (!isFinite(then)) return '-';
    const seconds = Math.max(0, Math.floor((now - then) / 1000));
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
}

export default function Messages() {
    const [params] = useSearchParams();
    const { setBot, botId } = useActiveBot();
    createRenderEffect(() => {
        const b = params.bot;
        if (typeof b === 'string' && b && b !== botId()) setBot(b);
    });
    return (
        <BotScoped>
            <MessagesInner />
        </BotScoped>
    );
}

function MessagesInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { botId, bot } = useActiveBot();
    const unread = useMessageUnread();
    const mutedContacts = useMutedContacts();
    const [params, setParams] = useSearchParams();
    const contacts = unread.contacts;
    const loadingContacts = unread.loadingContacts;

    const [search, setSearch] = createSignal('');
    const [selected, setSelected] = createSignal<string | null>(null);
    const [mobileView, setMobileView] = createSignal<'list' | 'chat'>('list');
    const [messages, setMessages] = createSignal<Msg[]>([]);
    const [loadingConvo, setLoadingConvo] = createSignal(false);
    const [draft, setDraft] = createSignal('');
    const [outbox, setOutbox] = createSignal<OutboxEntry[]>([]);
    const [now, setNow] = createSignal(Date.now());
    const [playerMap, setPlayerMap] = createSignal<Map<string, OnlinePlayer>>(new Map());
    const [notifEnabled, setNotifEnabled] = createSignal(true);
    const [notifBusy, setNotifBusy] = createSignal(false);
    const [muteBusy, setMuteBusy] = createSignal(false);
    let convoRef: HTMLDivElement | undefined;
    let composerRef: HTMLInputElement | undefined;

    const online = () => bot()?.state === 'Playing';

    const scrollConvoBottom = () => { if (convoRef) convoRef.scrollTop = convoRef.scrollHeight; };
    const isScrolledToBottom = () =>
        !convoRef || convoRef.scrollHeight - convoRef.scrollTop - convoRef.clientHeight <= AT_BOTTOM_SLACK_PX;

    const reflected = (live: Msg, server: Msg[]) =>
        server.some(s => s.isOutgoing === live.isOutgoing && s.body === live.body
            && Math.abs(new Date(s.createdAtUtc).getTime() - new Date(live.createdAtUtc).getTime()) < 30000);

    const mergePending = (server: Msg[], prev: Msg[]) => {
        const pending = prev.filter(m => m.id.startsWith('live-') && !reflected(m, server));
        const previousById = new Map(prev.map(m => [m.id, m]));
        return [...server.map(m => previousById.get(m.id) ?? m), ...pending];
    };

    const lastStoredOutgoingId = (list: Msg[]) => {
        for (let i = list.length - 1; i >= 0; i--) if (list[i].isOutgoing && !list[i].id.startsWith('live-')) return list[i].id;
        return null;
    };

    const visibleOutbox = createMemo(() => {
        const contact = selected();
        return contact ? outbox().filter(entry => sameContact(entry.contact, contact)) : [];
    });

    const settleOutboxEcho = (contact: string, echoedBody: string) => {
        const echoed = comparable(echoedBody);
        setOutbox(prev => {
            const matchIndex = prev.findIndex(entry => sameContact(entry.contact, contact)
                && (comparable(entry.body).includes(echoed) || echoed.includes(comparable(entry.body))));
            return matchIndex < 0 ? prev : prev.filter((_, i) => i !== matchIndex);
        });
    };

    // A stored outgoing row newer than the one seen at send time stands in for a missed live echo
    const settleOutboxFromServer = (contact: string, server: Msg[]) => {
        const newestOutgoing = lastStoredOutgoingId(server);
        setOutbox(prev => prev.filter(entry => !(entry.state === 'sent' && sameContact(entry.contact, contact)
            && entry.lastStoredOutgoingIdAtSend !== newestOutgoing)));
    };

    const loadConversation = async (scrollToEnd = false) => {
        const id = botId();
        const contact = selected();
        if (!id || !contact) { setMessages([]); return; }
        const res = await api.botMsg.getConversation(id, { contact });
        if (botId() !== id || selected() !== contact) return;
        if (isOk(res.data)) {
            const server = res.data.value.messages ?? [];
            const follow = scrollToEnd || isScrolledToBottom();
            setMessages(prev => mergePending(server, prev));
            settleOutboxFromServer(contact, server);
            if (follow) queueMicrotask(scrollConvoBottom);
        }
        setLoadingConvo(false);
    };

    const updateDraft = (text: string) => {
        setDraft(text);
        const id = botId();
        const contact = selected();
        if (id && contact) storeDraft(id, contact, text);
    };

    const loadDraft = () => {
        const id = botId();
        const contact = selected();
        setDraft(id && contact ? readStoredDraft(id, contact) : '');
    };

    const selectContact = (name: string) => {
        setSelected(name);
        loadDraft();
        void unread.markRead(name);
        setMobileView('chat');
        setLoadingConvo(true);
        setMessages([]);
        void loadConversation(true);
    };

    const send = async () => {
        const id = botId();
        const contact = selected();
        const text = draft().trim();
        if (!id || !contact || !text) return;
        const entry: OutboxEntry = {
            id: `out-${Date.now()}-${outbox().length}`,
            contact,
            body: text,
            state: 'sending',
            lastStoredOutgoingIdAtSend: lastStoredOutgoingId(messages()),
        };
        setOutbox(prev => [...prev, entry]);
        updateDraft('');
        queueMicrotask(scrollConvoBottom);
        composerRef?.focus();

        const res = await api.botMsg.send(id, { name: contact, message: text });
        if (isOk(res.data)) {
            setOutbox(prev => prev.map(pending => pending.id === entry.id ? { ...pending, state: 'sent' } : pending));
            return;
        }
        setOutbox(prev => prev.filter(pending => pending.id !== entry.id));
        if (!readStoredDraft(id, contact)) storeDraft(id, contact, text);
        if (!draft() && botId() === id && selected() === contact) setDraft(text);
        const error = firstError(res.data);
        toast?.error(error && SEND_ERROR_KEYS[error] ? t(SEND_ERROR_KEYS[error]) : error ?? t('MESSAGES_SEND_FAILED'));
    };

    const loadNotifPref = async () => {
        const id = botId();
        if (!id) return;
        const res = await api.botMsg.getNotificationPref(id);
        if (isOk(res.data)) setNotifEnabled(res.data.value.enabled);
    };

    const toggleNotif = async () => {
        const id = botId();
        if (!id || notifBusy()) return;
        const next = !notifEnabled();
        setNotifEnabled(next);
        setNotifBusy(true);
        const res = await api.botMsg.setNotificationPref(id, { enabled: next });
        setNotifBusy(false);
        if (isOk(res.data)) toast?.info(next ? t('MESSAGES_NOTIF_ENABLED') : t('MESSAGES_NOTIF_DISABLED'));
        else { setNotifEnabled(!next); toast?.error(t('MESSAGES_SEND_FAILED')); }
    };

    const toggleContactMute = async () => {
        const c = selected();
        if (!c || muteBusy()) return;
        const wasMuted = mutedContacts.isMuted(c);
        setMuteBusy(true);
        const ok = wasMuted ? await mutedContacts.unmute(c) : await mutedContacts.mute(c);
        setMuteBusy(false);
        if (ok) toast?.info(wasMuted ? t('MESSAGES_CONTACT_UNMUTED') : t('MESSAGES_CONTACT_MUTED'));
        else toast?.error(t('MESSAGES_SEND_FAILED'));
    };

    const filtered = () => {
        const q = search().trim().toLowerCase();
        if (!q) return contacts();
        return contacts().filter(c => strip(c.display).toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
    };

    const selectedContact = () => contacts().find(c => sameContact(c.name, selected() ?? ''));
    const playerStatus = (name: string) => playerMap().get(name.toLowerCase());
    const selectedStatus = createMemo(() => {
        const s = selected();
        return s ? playerStatus(s) : undefined;
    });

    createEffect(() => {
        if (botId()) untrack(() => {
            void loadNotifPref();
            loadDraft();
            if (selected()) { setMessages([]); void loadConversation(true); }
        });
    });

    createEffect(() => {
        const domain = bot()?.server;
        if (!domain) return;
        const unsub = subscribeDomainStream(domain, (snap) => {
            const m = new Map<string, OnlinePlayer>();
            for (const p of snap.players) m.set(p.name.toLowerCase(), p);
            setPlayerMap(m);
        });
        onCleanup(unsub);
    });

    createEffect(() => {
        const id = botId();
        if (!id) return;
        const unsubscribe = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type !== 'msg_received') return;
            const p = evt.payload as { contactName: string; display: string; body: string; isOutgoing: boolean; createdAtUtc: string };
            if (p.isOutgoing) settleOutboxEcho(p.contactName, p.body);
            const active = selected();
            if (active && sameContact(p.contactName, active)) {
                const follow = p.isOutgoing || isScrolledToBottom();
                setMessages(prev => [...prev, {
                    id: `live-${p.createdAtUtc}-${prev.length}`,
                    createdAtUtc: p.createdAtUtc,
                    isOutgoing: p.isOutgoing,
                    body: p.body,
                    display: p.display,
                }]);
                if (follow) queueMicrotask(scrollConvoBottom);
                if (!p.isOutgoing) void unread.markRead(active);
            }
        });
        let seenEpoch: number | null = null;
        const stopStream = subscribeBotEngineStream(id, (stream) => {
            if (seenEpoch !== null && stream.epoch !== seenEpoch && selected()) void loadConversation(false);
            seenEpoch = stream.epoch;
        });
        onCleanup(() => { unsubscribe(); stopStream(); });
    });

    // Reactive rather than onMount: a notification click routes here while the page is already open
    createEffect(() => {
        const deepLink = params.contact;
        if (!params.bot && !deepLink) return;
        if (typeof deepLink === 'string' && deepLink) untrack(() => selectContact(deepLink));
        setParams({ bot: undefined, contact: undefined }, { replace: true });
    });

    onMount(() => {
        void refreshPushSubscribed();
        useVisibilityPoll(() => {
            if (selected()) void loadConversation(false);
        }, FALLBACK_POLL_MS);
        const clock = window.setInterval(() => setNow(Date.now()), 1000);
        onCleanup(() => clearInterval(clock));
    });

    return (
        <div classList={{ [styles.container]: true, [styles.mobileList]: mobileView() === 'list', [styles.mobileChat]: mobileView() === 'chat' }}>
            <div class={styles.header}>
                <div class={styles.headerTitle}>
                    <h1><Text val="MESSAGES_TITLE" /></h1>
                    <Show when={!loadingContacts()}>
                        <span class={styles.headerCount}>
                            {contacts().length} <Text val="MESSAGES_CONVERSATIONS" />
                            <Show when={unread.unreadCount() > 0}>
                                {' · '}{unread.unreadCount()} <Text val="MESSAGES_UNREAD" />
                            </Show>
                        </span>
                    </Show>
                </div>
                <Show
                    when={pushSubscribed()}
                    fallback={
                        <button
                            class="btn btn-secondary"
                            title={t('MESSAGES_NOTIF_SETUP')}
                            onClick={() => navigate('/settings')}
                        >
                            <i class="fa-solid fa-bell-slash" style={{ color: 'var(--dark-text-muted)' }} />
                            <span class={styles.notifLabel}><Text val="MESSAGES_NOTIF_LABEL_SETUP" /></span>
                        </button>
                    }
                >
                    <button
                        class="btn btn-secondary"
                        title={notifEnabled() ? t('MESSAGES_NOTIF_ON') : t('MESSAGES_NOTIF_OFF')}
                        onClick={() => void toggleNotif()}
                        disabled={notifBusy()}
                    >
                        <i class={notifEnabled() ? 'fa-solid fa-bell' : 'fa-solid fa-bell-slash'} style={{ color: notifEnabled() ? undefined : 'var(--dark-text-muted)' }} />
                        <span class={styles.notifLabel}>
                            <Show when={notifEnabled()} fallback={<Text val="MESSAGES_NOTIF_LABEL_OFF" />}>
                                <Text val="MESSAGES_NOTIF_LABEL_ON" />
                            </Show>
                        </span>
                    </button>
                </Show>
            </div>

            <div class={styles.layout}>
                <aside class={styles.sidebar}>
                    <SearchInput value={search()} onInput={setSearch} placeholder={t('MESSAGES_SEARCH')} />
                    <Show when={!loadingContacts()} fallback={
                        <div class={styles.skeletonList}>
                            <For each={[0, 1, 2, 3]}>{() => <div class={styles.skeletonRow} />}</For>
                        </div>
                    }>
                        <Show when={filtered().length > 0} fallback={
                            <EmptyState illustration="players" title={t('MESSAGES_NO_CONTACTS')} />
                        }>
                            <div class={styles.contacts}>
                                <For each={filtered()}>{(c) => {
                                    const ps = () => playerStatus(c.name);
                                    const isUnread = () => unread.isUnread(c);
                                    return (
                                        <button
                                            classList={{ [styles.contact]: true, [styles.active]: sameContact(c.name, selected() ?? ''), [styles.unread]: isUnread() }}
                                            onClick={() => selectContact(c.name)}
                                        >
                                            <PlayerHeadAvatar name={c.name} size={36} showStatus={!!ps()} online={ps()?.currentlyOnline} />
                                            <span class={styles.contactBody}>
                                                <span class={styles.contactTopRow}>
                                                    <span class={styles.contactName}>{formatMinecraftText(c.display)}</span>
                                                    <Show when={mutedContacts.isMuted(c.name)}>
                                                        <i class="fa-solid fa-user-slash" title={t('MESSAGES_CONTACT_MUTED_BADGE')} style={{ color: 'var(--dark-text-muted)', "font-size": 'var(--font-size-xs)' }} />
                                                    </Show>
                                                    <span class={styles.contactTime}>
                                                        <RelativeTime value={c.lastAtUtc} />
                                                    </span>
                                                </span>
                                                <span class={styles.contactBottomRow}>
                                                    <span class={styles.contactPreview}>
                                                        <Show when={c.lastIsOutgoing}><Text val="MESSAGES_PREVIEW_YOU" />{' '}</Show>
                                                        {strip(c.lastBody)}
                                                    </span>
                                                    <Show when={isUnread()}>
                                                        <span class={styles.unreadBadge}>{c.unreadCount}</span>
                                                    </Show>
                                                </span>
                                            </span>
                                        </button>
                                    );
                                }}</For>
                            </div>
                        </Show>
                    </Show>
                </aside>

                <section class={styles.main}>
                    <Show when={selected()} fallback={
                        <EmptyState icon="fa-comment-dots" title={t('MESSAGES_SELECT_CONTACT')} />
                    }>
                        <div class={styles.convoHeader}>
                            <button class={`btn btn-icon ${styles.backBtn}`} title={t('MESSAGES_BACK')} onClick={() => setMobileView('list')}>
                                <i class="fa-solid fa-arrow-left" />
                            </button>
                            <PlayerHeadAvatar name={selected()!} size={32} showStatus={!!selectedStatus()} online={selectedStatus()?.currentlyOnline} />
                            <span class={styles.convoHeaderIdentity}>
                                <span class={styles.convoHeaderName}>{formatMinecraftText(selectedContact()?.display ?? selected()!)}</span>
                                <Show when={selectedStatus()}>
                                    <span class={styles.headerStatusText}>
                                        {selectedStatus()!.currentlyOnline
                                            ? `${t('MESSAGES_PLAYER_ONLINE')} · ${elapsed(selectedStatus()!.currentSessionStart, now())}`
                                            : `${t('MESSAGES_PLAYER_OFFLINE')} · ${elapsed(selectedStatus()!.lastSeen, now())}`}
                                    </span>
                                </Show>
                            </span>
                            <button
                                class={`btn btn-secondary btn-sm ${styles.muteBtn}`}
                                title={mutedContacts.isMuted(selected()!) ? t('MESSAGES_CONTACT_UNMUTE') : t('MESSAGES_CONTACT_MUTE')}
                                onClick={() => void toggleContactMute()}
                                disabled={muteBusy()}
                            >
                                <i
                                    class="fa-solid fa-user-slash"
                                    style={{ color: mutedContacts.isMuted(selected()!) ? 'var(--warning-500)' : undefined }}
                                />
                                <span class={styles.muteLabel}>
                                    <Show when={mutedContacts.isMuted(selected()!)} fallback={<Text val="MESSAGES_CONTACT_MUTE_LABEL" />}>
                                        <Text val="MESSAGES_CONTACT_MUTED_BADGE" />
                                    </Show>
                                </span>
                            </button>
                        </div>
                        <div class={styles.convo} ref={convoRef}>
                            <Show when={!loadingConvo()} fallback={
                                <div class={styles.center}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>
                            }>
                                <Show when={messages().length > 0 || visibleOutbox().length > 0} fallback={
                                    <EmptyState icon="fa-comment-slash" title={t('MESSAGES_NO_MESSAGES')} />
                                }>
                                    <For each={messages()}>{(m) =>
                                        <div classList={{ [styles.bubble]: true, [styles.out]: m.isOutgoing, [styles.in]: !m.isOutgoing }}>
                                            <div class={styles.bubbleBody}>{formatMinecraftText(m.body)}</div>
                                            <div class={styles.bubbleTime}><RelativeTime value={m.createdAtUtc} showAbsolute/></div>
                                        </div>
                                    }</For>
                                    <For each={visibleOutbox()}>{(entry) =>
                                        <div classList={{ [styles.bubble]: true, [styles.out]: true, [styles.pending]: entry.state === 'sending' }}>
                                            <div class={styles.bubbleBody}>{formatMinecraftText(entry.body)}</div>
                                            <div class={styles.bubbleTime}>
                                                <Show when={entry.state === 'sending'} fallback={<><i class="fa-solid fa-check" /> <Text val="MESSAGES_SENT" /></>}>
                                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="MESSAGES_SENDING" />
                                                </Show>
                                            </div>
                                        </div>
                                    }</For>
                                </Show>
                            </Show>
                        </div>
                        <form class={styles.composer} onSubmit={(e) => { e.preventDefault(); void send(); }}>
                            <input
                                ref={composerRef}
                                type="text"
                                value={draft()}
                                onInput={(e) => updateDraft(e.currentTarget.value)}
                                placeholder={online() ? t('MESSAGES_COMPOSER_PLACEHOLDER') : t('MESSAGES_BOT_OFFLINE')}
                                disabled={!online()}
                            />
                            <button type="submit" class="btn btn-primary" disabled={!online() || !draft().trim()}>
                                <Text val="MESSAGES_SEND" />
                            </button>
                        </form>
                    </Show>
                </section>
            </div>
        </div>
    );
}
